import React, { useState, useEffect } from 'react';
import api from '../api';
import { Settings, Save, RotateCcw, Loader2, CheckCircle, AlertCircle, Layers, Plus, X } from 'lucide-react';

/**
 * Admin panel for S&D zone engine settings.
 * Fetches schema + current values from backend, renders a grouped form.
 * Supports per-symbol overrides.
 */
export default function SDZoneSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [schema, setSchema] = useState([]);
  const [settings, setSettings] = useState({});
  const [isDefault, setIsDefault] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Per-symbol override state
  const [symbolOverrides, setSymbolOverrides] = useState([]);
  const [activeSymbol, setActiveSymbol] = useState('');
  const [symbolInput, setSymbolInput] = useState('');
  const [hasSymbolOverride, setHasSymbolOverride] = useState(false);
  const [symbolUpdatedAt, setSymbolUpdatedAt] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchOverrides();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/sd-zones/settings');
      const data = res.data;
      setSchema(data.schema || []);
      setSettings(data.settings || {});
      setIsDefault(data.is_default);
      setUpdatedAt(data.updated_at);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await api.get('/admin/sd-zones/settings/symbols');
      setSymbolOverrides(res.data?.overrides || []);
    } catch { /* optional */ }
  };

  const loadSymbolSettings = async (symbol) => {
    if (!symbol) return;
    try {
      const res = await api.get(`/admin/sd-zones/settings/${symbol}`);
      const data = res.data;
      setSettings(data.settings || {});
      setHasSymbolOverride(data.has_override);
      setSymbolUpdatedAt(data.updated_at);
      setActiveSymbol(symbol);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load symbol settings: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const exitSymbolMode = () => {
    setActiveSymbol('');
    setHasSymbolOverride(false);
    setSymbolUpdatedAt(null);
    fetchSettings(); // reload global
  };

  const handleAddSymbol = (e) => {
    e.preventDefault();
    const sym = symbolInput.trim().toUpperCase();
    if (!sym) return;
    if (symbolOverrides.some(o => o.symbol === sym)) {
      loadSymbolSettings(sym);
    } else {
      loadSymbolSettings(sym);
    }
    setSymbolInput('');
  };

  const handleChange = (name, value) => {
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      if (activeSymbol) {
        await api.put(`/admin/sd-zones/settings/${activeSymbol}`, settings);
        setMessage({ type: 'success', text: `${activeSymbol} override saved. Zones for this pair will use these values.` });
        await loadSymbolSettings(activeSymbol);
        await fetchOverrides();
      } else {
        await api.put('/admin/sd-zones/settings', settings);
        setMessage({ type: 'success', text: 'Global settings saved. New zones will use these values.' });
        await fetchSettings();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (activeSymbol) {
      if (!window.confirm(`Remove the per-symbol override for ${activeSymbol}? It will fall back to global settings.`)) return;
      try {
        setResetting(true);
        setMessage({ type: '', text: '' });
        await api.post(`/admin/sd-zones/settings/${activeSymbol}/reset`);
        setMessage({ type: 'success', text: `${activeSymbol} override removed. Now using global settings.` });
        await fetchOverrides();
        exitSymbolMode();
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to reset: ' + (err.response?.data?.detail || err.message) });
      } finally {
        setResetting(false);
      }
    } else {
      if (!window.confirm('Reset to Pine-script defaults? This will delete your custom settings.')) return;
      try {
        setResetting(true);
        setMessage({ type: '', text: '' });
        await api.post('/admin/sd-zones/settings/reset');
        setMessage({ type: 'success', text: 'Reset to defaults.' });
        await fetchSettings();
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to reset: ' + (err.response?.data?.detail || err.message) });
      } finally {
        setResetting(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-blue-500" size={40} />
      </div>
    );
  }

  // Group fields by their 'group' metadata
  const groups = {};
  schema.forEach(f => {
    const g = f.group || 'General';
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings size={22} className="text-blue-500" /> S&D Zone Engine Settings
          </h3>
          {activeSymbol ? (
            <p className="text-sm text-gray-500 mt-1">
              Editing override for <span className="font-semibold text-blue-600 dark:text-blue-400">{activeSymbol}</span>
              {hasSymbolOverride
                ? ` — updated ${new Date(symbolUpdatedAt).toLocaleString()}`
                : ' — no override yet, showing global values'}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">
              {isDefault ? 'Using Pine-script defaults.' : `Last updated: ${new Date(updatedAt).toLocaleString()}`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {activeSymbol && (
            <button
              onClick={exitSymbolMode}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium"
            >
              <X size={16} /> Back to Global
            </button>
          )}
          <button
            onClick={handleReset}
            disabled={resetting || (!activeSymbol && isDefault)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resetting ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
            {activeSymbol ? 'Remove Override' : 'Reset to Defaults'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Per-symbol override management (only shown in global mode) */}
      {!activeSymbol && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 shadow-sm">
          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Layers size={14} /> Per-Pair Overrides
          </h4>

          {/* Add new symbol */}
          <form onSubmit={handleAddSymbol} className="flex gap-2 mb-4">
            <input
              type="text"
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value)}
              placeholder="e.g. BTCUSD"
              className="flex-1 max-w-xs px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={!symbolInput.trim()}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus size={14} /> Edit Pair
            </button>
          </form>

          {/* Existing overrides list */}
          {symbolOverrides.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {symbolOverrides.map((ov) => (
                <button
                  key={ov.symbol}
                  onClick={() => loadSymbolSettings(ov.symbol)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium transition-colors"
                >
                  <Layers size={12} /> {ov.symbol}
                  <span className="text-[10px] text-blue-400 dark:text-blue-500">
                    {new Date(ov.updated_at).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No per-pair overrides yet. All pairs use global settings.</p>
          )}
        </div>
      )}

      {/* Symbol override banner */}
      {activeSymbol && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
          hasSymbolOverride
            ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400'
            : 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
        }`}>
          <Layers size={16} />
          {hasSymbolOverride
            ? `${activeSymbol} has a custom override. Changes here apply only to this pair.`
            : `No override for ${activeSymbol} yet. Save to create one. Currently showing global values.`}
        </div>
      )}

      {/* Feedback message */}
      {message.text && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400'
            : 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Grouped form */}
      {Object.entries(groups).map(([groupName, fields]) => (
        <div key={groupName} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">{groupName}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map(field => (
              <FormField
                key={field.name}
                field={field}
                value={settings[field.name]}
                onChange={handleChange}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FormField({ field, value, onChange }) {
  const { name, type, label, help, min, max, step, options, default: defaultValue } = field;

  const handleChange = (e) => {
    let v = e.target.value;
    if (type === 'int') v = parseInt(v, 10);
    else if (type === 'float') v = parseFloat(v);
    else if (type === 'bool') v = e.target.checked;
    onChange(name, v);
  };

  const inputId = `sd-setting-${name}`;

  // Boolean: toggle
  if (type === 'bool') {
    return (
      <div className="flex items-start justify-between gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <div className="flex-1">
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            {label}
          </label>
          {help && <p className="text-xs text-gray-400 mt-0.5">{help}</p>}
        </div>
        <input
          id={inputId}
          type="checkbox"
          checked={!!value}
          onChange={handleChange}
          className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
      </div>
    );
  }

  // Select: dropdown
  if (options && options.length) {
    return (
      <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
        {help && <p className="text-xs text-gray-400 mb-2">{help}</p>}
        <select
          id={inputId}
          value={value || defaultValue}
          onChange={handleChange}
          className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  // Number: input with min/max/step
  const inputType = type === 'float' ? 'number' : 'number';
  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      {help && <p className="text-xs text-gray-400 mb-2">{help}</p>}
      <input
        id={inputId}
        type={inputType}
        min={min}
        max={max}
        step={step || (type === 'float' ? 0.1 : 1)}
        value={value ?? defaultValue}
        onChange={handleChange}
        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
      />
      {(min !== undefined || max !== undefined) && (
        <p className="text-[10px] text-gray-400 mt-1">
          Range: {min ?? 'any'} – {max ?? 'any'}
          {step && ` (step ${step})`}
        </p>
      )}
    </div>
  );
}
